/**
 * AuthContext — Firebase Auth state + platform role
 *
 * Provides:
 *   user        — Firebase User (null if logged out)
 *   platformUser — role + client_id from /auth/me (null until resolved)
 *   loading     — true while Firebase is resolving initial auth state
 *   idToken     — current ID token string (refreshed automatically)
 *   signOut     — callable to sign out
 *
 * Token is kept fresh via onIdTokenChanged, which fires on sign-in, sign-out,
 * and each automatic Firebase token refresh (~55 min). idToken in context is
 * always valid — no 401s from stale tokens.
 * useAuth() is the consumer hook — components import that, not the context directly.
 */

import {
  createContext, useContext, useEffect, useState, useCallback, useRef,
  type ReactNode,
} from 'react'
import {
  onIdTokenChanged, signOut as fbSignOut,
  type User,
} from 'firebase/auth'
import { auth } from '../lib/firebase'

const API_BASE = import.meta.env.VITE_API_URL
  || 'https://clutch-ocm-scorer-uqrk7imnlq-uc.a.run.app'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type UserRole = 'clutch_admin' | 'clutch_consultant' | 'client_admin'

export interface PlatformUser {
  id:           number
  firebase_uid: string
  email:        string
  display_name: string | null
  role:         UserRole
  client_id:    number | null
  client_name:  string | null
  active_flag:  boolean
}

interface AuthContextValue {
  user:          User | null
  platformUser:  PlatformUser | null
  loading:       boolean
  idToken:       string | null
  signOut:       () => Promise<void>
  refreshUser:   () => Promise<void>
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthCtx = createContext<AuthContextValue | null>(null)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,         setUser]         = useState<User | null>(null)
  const [platformUser, setPlatformUser] = useState<PlatformUser | null>(null)
  const [idToken,      setIdToken]      = useState<string | null>(null)
  const [loading,      setLoading]      = useState(true)
  // Tracks whether we've already loaded the platform user for the current session.
  // Prevents a loading flash + unnecessary /auth/me call on periodic token refreshes.
  const platformUserLoaded = useRef(false)

  // Fetch role from /auth/me after Firebase auth resolves.
  // Retries once with a forced token refresh if we get a 401 — handles the
  // near-expiry edge case where the cached token is stale on first page load.
  const loadPlatformUser = useCallback(async (firebaseUser: User) => {
    try {
      let token = await firebaseUser.getIdToken()
      setIdToken(token)
      let res = await fetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok && res.status === 401) {
        // Token may have just expired — force a refresh and retry once
        token = await firebaseUser.getIdToken(true)
        setIdToken(token)
        res = await fetch(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      }
      if (res.ok) {
        const data: PlatformUser = await res.json()
        setPlatformUser(data)
      } else {
        setPlatformUser(null)
      }
    } catch {
      setPlatformUser(null)
    }
  }, [])

  useEffect(() => {
    // onIdTokenChanged fires on sign-in, sign-out, AND automatic token refreshes
    // (~every 55 min). This keeps idToken in context from going stale.
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser)
      if (firebaseUser) {
        // Always grab a fresh token — Firebase returns cached if still valid,
        // refreshed if expired. This is the key fix for the 1-hour 401 problem.
        const token = await firebaseUser.getIdToken()
        setIdToken(token)

        // Only hit /auth/me on actual sign-in, not on periodic token refreshes.
        if (!platformUserLoaded.current) {
          setLoading(true)
          await loadPlatformUser(firebaseUser)
          platformUserLoaded.current = true
          setLoading(false)
        }
      } else {
        // Sign-out: clear everything and reset the loaded flag.
        platformUserLoaded.current = false
        setPlatformUser(null)
        setIdToken(null)
        setLoading(false)
      }
    })
    return unsubscribe
  }, [loadPlatformUser])

  const handleSignOut = useCallback(async () => {
    await fbSignOut(auth)
    setUser(null)
    setPlatformUser(null)
    setIdToken(null)
  }, [])

  const refreshUser = useCallback(async () => {
    if (user) await loadPlatformUser(user)
  }, [user, loadPlatformUser])

  return (
    <AuthCtx.Provider value={{
      user, platformUser, loading, idToken,
      signOut: handleSignOut, refreshUser,
    }}>
      {children}
    </AuthCtx.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthCtx)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

// ---------------------------------------------------------------------------
// Helper utilities
// ---------------------------------------------------------------------------

export function isClutchRole(pu: PlatformUser | null): boolean {
  return pu?.role === 'clutch_admin' || pu?.role === 'clutch_consultant'
}

export function isAdmin(pu: PlatformUser | null): boolean {
  return pu?.role === 'clutch_admin'
}

export function isClientAdmin(pu: PlatformUser | null): boolean {
  return pu?.role === 'client_admin'
}
